"use client";

import { ChartBar as BarChart3, Boxes, Check, ShoppingCart, Truck, Users, Wallet } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Capabilities() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const modules = [{
    icon: Wallet,
    name: "Automotive Sales",
    description: "Strategic market penetration for automotive components, ensuring your parts reach the assembly lines that matter most.",
    capabilities: ["OEM relationship management", "Tier-1 supply chain integration", "Technical specification alignment", "Volume forecasting & planning", "Aftermarket channel development"]
  }, {
    icon: Truck,
    name: "Metal Fabrication",
    description: "Connecting high-precision metal fabrication capabilities with industrial buyers requiring exact tolerances and reliability.",
    capabilities: ["Custom component sourcing", "Material grade compliance", "Production capacity matching", "Quality assurance oversight", "Lead-time optimization"]
  }, {
    icon: Users,
    name: "Industrial Safety",
    description: "Representing safety-critical equipment to ensure industrial environments meet rigorous compliance and protection standards.",
    capabilities: ["Safety protocol integration", "Regulatory compliance support", "Equipment demonstration", "Workplace hazard assessment", "Safety training coordination"]
  }, {
    icon: ShoppingCart,
    name: "Shipbuilding Supply",
    description: "Navigating the complex procurement cycles of the maritime industry to secure long-term contracts for your components.",
    capabilities: ["Maritime specification adherence", "Naval architecture alignment", "Large-scale project bidding", "Logistics for heavy components", "Fleet maintenance support"]
  }, {
    icon: Boxes,
    name: "Strategic Sourcing",
    description: "Guided procurement strategies that bridge the gap between manufacturer capabilities and buyer requirements.",
    capabilities: ["Vendor qualification & selection", "Contract lifecycle management", "Supply chain risk mitigation", "Cost-structure analysis", "Category spend intelligence"]
  }, {
    icon: BarChart3,
    name: "Market Intelligence",
    description: "Data-driven insights that turn industrial trends into actionable sales strategies for your manufacturing firm.",
    capabilities: ["Competitive landscape analysis", "Demand signal monitoring", "Cross-industry trend modeling", "Opportunity identification", "Performance reporting"]
  }];
  return <section data-section-id="11601" id="representation-capabilities" className="bg-background py-24 md:py-32">
      <div className="mx-auto w-full max-w-[1240px] px-5 md:px-8">
        {}
        <div ref={intro.ref} className={`mx-auto max-w-[720px] text-center transition-all duration-700 ease-out ${intro.isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <h2 className="font-default font-serif font-normal text-foreground text-[clamp(2rem,1.2rem+3.2vw,3.5rem)] leading-[1.08] tracking-[-0.018em]">Multiple industries, <span className="font-serif italic text-primary" data-ast-id="f:/src/pages/Services/sections/Capabilities.tsx|sig:tf4vm" data-ast-start="3725" data-ast-end="3784">one</span> unified sales engine.</h2>
          <p className="mt-6 font-sans text-[clamp(1.0625rem,1rem+0.3vw,1.1875rem)] leading-[1.6] text-muted-foreground">
            MetPro Partners leverages a singular technical framework to represent your 
            manufacturing capabilities. We bridge the gap between production and 
            procurement with precision, authority, and industrial focus.
          </p>
        </div>

        {}
        <div className="mt-16 h-px w-full bg-border" aria-hidden="true" />

        {}
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2">
          {modules.map((mod, i) => {
          const Icon = mod.icon;
          return <article data-index={i} key={mod.name} className="group border border-border bg-card p-8 md:p-9 transition-all duration-[240ms] ease-out hover:-translate-y-0.5 hover:border-primary">
                <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-6">
                  {}
                  <div className="md:col-span-5">
                    <Icon className="size-7 text-primary" strokeWidth={1.25} aria-hidden="true" />
                    <h3 className="font-default mt-5 font-sans text-[clamp(1.25rem,1.05rem+0.8vw,1.625rem)] font-medium leading-[1.25] tracking-[-0.011em] text-foreground">
                      {mod.name}
                    </h3>
                    <p className="mt-3 font-sans text-[0.9375rem] leading-[1.65] text-muted-foreground">
                      {mod.description}
                    </p>
                    <Button asChild variant="link" className="mt-6 h-auto p-0 font-sans text-[0.9375rem] font-normal text-foreground hover:no-underline">
                      <Link to="/ServicesPage">
                        <span className="border-b border-border pb-0.5 transition-colors duration-200 group-hover:border-primary">
                          {"Service details \u2192"}
                        </span>
                      </Link>
                    </Button>
                  </div>

                  {}
                  <div className="md:col-span-7">
                    <p className="font-sans text-[0.75rem] font-medium uppercase tracking-[0.14em] leading-[1.4] text-muted-foreground">
                      Core capabilities
                    </p>
                    <ul className="mt-5 space-y-3">
                      {mod.capabilities.map((cap, j) => <li data-index={j} key={cap} className="flex items-start gap-3">
                          <Check className="mt-1 size-4 shrink-0 text-primary" strokeWidth={1.25} aria-hidden="true" />
                          <span className="font-sans text-[0.9375rem] leading-[1.5] text-foreground">
                            {cap}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}